import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { loaderDecrement, loaderIncrement } from "../services/globalLoader";

const getDesignationId = (d) => d?.designationId || d?._id || d?.id || "";
const getDesignationName = (d) => d?.designationName || d?.name || "";

const pickList = (data) => {
  if (!data || typeof data !== "object") return [];
  if (Array.isArray(data.designations)) return data.designations;
  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(data.result)) return data.result;
  if (Array.isArray(data.list)) return data.list;
  return [];
};

const pickOne = (data) => {
  if (!data || typeof data !== "object") return null;
  if (data.designation && typeof data.designation === "object") return data.designation;
  if (data.result && typeof data.result === "object" && !Array.isArray(data.result)) {
    return data.result;
  }
  if (data.data && typeof data.data === "object" && !Array.isArray(data.data)) {
    return data.data;
  }
  if (getDesignationId(data) || getDesignationName(data)) return data;
  return null;
};

const Designation = () => {
  const API_BASE_URL = process.env.REACT_APP_API_URL;
  const userId = useMemo(() => localStorage.getItem("userid") || "", []);

  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 1
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [selected, setSelected] = useState(null);
  const [designationName, setDesignationName] = useState("");
  const [loadingItem, setLoadingItem] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchList = useCallback(async () => {
    if (!API_BASE_URL) {
      toast.error("Missing REACT_APP_API_URL in environment");
      return;
    }

    let didIncrement = false;
    try {
      const payload = {
        action: "designation.getAll",
        userId,
        userid: userId,
        page,
        limit
      };
      const q = String(search || "").trim();
      if (q) payload.search = q;

      loaderIncrement();
      didIncrement = true;
      const response = await axios.post(`${API_BASE_URL}`, payload);
      const data = response?.data;

      if (data?.status === "success" || data?.success === true) {
        const list = pickList(data);
        setItems(Array.isArray(list) ? list : []);
        const pag = data.pagination && typeof data.pagination === "object" ? data.pagination : {};
        setPagination({
          page: Number(pag.page) || page,
          limit: Number(pag.limit) || limit,
          total: Number(pag.total) || (Array.isArray(list) ? list.length : 0),
          pages: Math.max(1, Number(pag.pages) || 1)
        });
      } else {
        toast.error(data?.message || "Failed to load designations");
        setItems([]);
      }
    } catch (error) {
      const msg =
        error?.response?.data?.message || error?.message || "Something went wrong";
      toast.error(msg);
      setItems([]);
    } finally {
      if (didIncrement) loaderDecrement();
    }
  }, [API_BASE_URL, page, limit, search, userId]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      setSearch(searchInput.trim());
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const applySearch = (e) => {
    e?.preventDefault?.();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelected(null);
    setDesignationName("");
    setLoadingItem(false);
    setSaving(false);
  };

  const openCreateModal = () => {
    setModalMode("create");
    setSelected(null);
    setDesignationName("");
    setLoadingItem(false);
    setModalOpen(true);
  };

  const openDesignationModal = async (mode, row) => {
    const designationId = getDesignationId(row);
    if (!designationId) {
      toast.error("Missing designation id");
      return;
    }
    if (!API_BASE_URL) {
      toast.error("Missing REACT_APP_API_URL in environment");
      return;
    }

    setModalMode(mode);
    setModalOpen(true);
    setLoadingItem(true);
    setSelected(null);
    setDesignationName("");

    let didIncrement = false;
    try {
      loaderIncrement();
      didIncrement = true;
      const response = await axios.post(`${API_BASE_URL}`, {
        action: "designation.getById",
        userId,
        userid: userId,
        designationId
      });
      const data = response?.data;

      if (data?.status === "success" || data?.success === true) {
        const item = pickOne(data);
        if (!item) {
          toast.error(data?.message || "Designation not found");
          closeModal();
          return;
        }
        setSelected(item);
        setDesignationName(getDesignationName(item));
      } else {
        toast.error(data?.message || "Failed to load designation");
        closeModal();
      }
    } catch (error) {
      const msg =
        error?.response?.data?.message || error?.message || "Something went wrong";
      toast.error(msg);
      closeModal();
    } finally {
      if (didIncrement) loaderDecrement();
      setLoadingItem(false);
    }
  };

  const handleCreate = async () => {
    const name = designationName.trim();
    if (!name) {
      toast.error("Designation name is required");
      return;
    }
    if (!API_BASE_URL) {
      toast.error("Missing REACT_APP_API_URL in environment");
      return;
    }

    setSaving(true);
    let didIncrement = false;
    try {
      loaderIncrement();
      didIncrement = true;
      const response = await axios.post(`${API_BASE_URL}`, {
        action: "designation.create",
        userId,
        userid: userId,
        designationName: name
      });
      const data = response?.data;

      if (data?.status === "success" || data?.success === true) {
        toast.success(data?.message || "Designation created successfully");
        closeModal();
        fetchList();
      } else {
        toast.error(data?.message || "Failed to create designation");
      }
    } catch (error) {
      const msg =
        error?.response?.data?.message || error?.message || "Something went wrong";
      toast.error(msg);
    } finally {
      if (didIncrement) loaderDecrement();
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    const designationId = getDesignationId(selected);
    const name = designationName.trim();
    if (!designationId) {
      toast.error("Missing designation id");
      return;
    }
    if (!name) {
      toast.error("Designation name is required");
      return;
    }
    if (!API_BASE_URL) {
      toast.error("Missing REACT_APP_API_URL in environment");
      return;
    }

    setSaving(true);
    let didIncrement = false;
    try {
      loaderIncrement();
      didIncrement = true;
      const response = await axios.post(`${API_BASE_URL}`, {
        action: "designation.update",
        userId,
        userid: userId,
        designationId,
        designationName: name
      });
      const data = response?.data;

      if (data?.status === "success" || data?.success === true) {
        toast.success(data?.message || "Designation updated successfully");
        closeModal();
        fetchList();
      } else {
        toast.error(data?.message || "Failed to update designation");
      }
    } catch (error) {
      const msg =
        error?.response?.data?.message || error?.message || "Something went wrong";
      toast.error(msg);
    } finally {
      if (didIncrement) loaderDecrement();
      setSaving(false);
    }
  };

  const totalPages = Math.max(1, Number(pagination?.pages || 1));
  const currentPage = Math.min(Math.max(1, Number(page) || 1), totalPages);
  const pageNum = Number(pagination?.page) || currentPage;
  const pageLimit = Number(pagination?.limit) || limit;

  const pageButtons = useMemo(() => {
    const windowSize = 5;
    const half = Math.floor(windowSize / 2);
    let start = Math.max(1, currentPage - half);
    let end = Math.min(totalPages, start + windowSize - 1);
    start = Math.max(1, end - windowSize + 1);
    const pages = [];
    for (let p = start; p <= end; p += 1) pages.push(p);
    return pages;
  }, [currentPage, totalPages]);

  const modalTitle =
    modalMode === "create"
      ? "Add Designation"
      : modalMode === "edit"
        ? "Edit Designation"
        : "View Designation";

  const rows = Array.isArray(items) ? items : [];

  return (
    <div className="employee-form">
      <div className="content-header">
        <div
          className="container-fluid d-flex justify-content-between align-items-end flex-wrap"
          style={{ gap: 12 }}
        >
          <div>
            <h1 className="m-0 txt_lft">Designation</h1>
            <p style={{ margin: 0, color: "#6c757d", fontSize: 14 }}>
              Manage community designations.
            </p>
          </div>
          <button type="button" className="btn btn-primary" onClick={openCreateModal}>
            Add Designation
          </button>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <div className="card">
            <div className="card-body">
              <div className="role-table-box">
                <div className="role-table-top">
                  <form onSubmit={applySearch} className="role-table-search-wrap">
                    <input
                      type="text"
                      className="form-control role-table-search"
                      placeholder="Search designations..."
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                    />
                  </form>
                </div>

                <div className="table-responsive">
                  <table className="table table-hover role-table">
                    <thead>
                      <tr>
                        <th style={{ width: 90 }}>
                          <span className="role-th">Sl No.</span>
                        </th>
                        <th>
                          <span className="role-th">Designation</span>
                        </th>
                        <th style={{ width: 140 }}>
                          <span className="role-th">Action</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.length ? (
                        rows.map((row, idx) => (
                          <tr key={String(getDesignationId(row) || `row-${idx}`)}>
                            <td>{(pageNum - 1) * pageLimit + idx + 1}</td>
                            <td>{getDesignationName(row) || "—"}</td>
                            <td>
                              <div className="d-flex" style={{ gap: 8 }}>
                                <button
                                  type="button"
                                  className="role-action-btn role-action-view"
                                  title="View"
                                  onClick={() => openDesignationModal("view", row)}
                                >
                                  <i className="fa-solid fa-eye" aria-hidden="true" />
                                </button>
                                <button
                                  type="button"
                                  className="role-action-btn role-action-edit"
                                  title="Edit"
                                  onClick={() => openDesignationModal("edit", row)}
                                >
                                  <i className="fa-solid fa-pen-to-square" aria-hidden="true" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={3} className="text-center text-muted">
                            {search ? "No designations found" : "No designations"}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div
                  className="role-table-footer d-flex justify-content-between align-items-center flex-wrap"
                  style={{ gap: 10 }}
                >
                  <div className="d-flex align-items-center" style={{ gap: 10 }}>
                    <span className="text-muted" style={{ fontSize: 12 }}>
                      Show:
                    </span>
                    <select
                      className="form-control"
                      style={{ width: 90 }}
                      value={limit}
                      onChange={(e) => {
                        setPage(1);
                        setLimit(Number(e.target.value));
                      }}
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                  </div>

                  <div className="btn-group">
                    <button
                      type="button"
                      className="btn btn-light"
                      disabled={currentPage <= 1}
                      onClick={() => setPage(1)}
                    >
                      &laquo;
                    </button>
                    <button
                      type="button"
                      className="btn btn-light"
                      disabled={currentPage <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      &lsaquo;
                    </button>
                    {pageButtons.map((p) => (
                      <button
                        key={p}
                        type="button"
                        className={`btn ${p === currentPage ? "btn-primary" : "btn-light"}`}
                        onClick={() => setPage(p)}
                      >
                        {p}
                      </button>
                    ))}
                    <button
                      type="button"
                      className="btn btn-light"
                      disabled={currentPage >= totalPages}
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    >
                      &rsaquo;
                    </button>
                    <button
                      type="button"
                      className="btn btn-light"
                      disabled={currentPage >= totalPages}
                      onClick={() => setPage(totalPages)}
                    >
                      &raquo;
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {modalOpen ? (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16
          }}
          onClick={closeModal}
        >
          <div
            className="card app-modal-card"
            style={{ width: "min(480px, 96vw)", maxHeight: "90vh", overflow: "auto" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="card-header app-modal-header">
              <div className="app-modal-title">{modalTitle}</div>
              <button
                type="button"
                className="app-modal-close"
                onClick={closeModal}
                disabled={saving}
                aria-label="Close"
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>

            <div className="card-body">
              {loadingItem ? (
                <div className="text-center text-muted" style={{ padding: 24 }}>
                  Loading...
                </div>
              ) : (
                <div className="form-group mb-0">
                  <label className="form-label">
                    Designation Name{" "}
                    {modalMode !== "view" ? (
                      <span className="required-star">*</span>
                    ) : null}
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. Secretary"
                    value={designationName}
                    onChange={(e) => setDesignationName(e.target.value)}
                    disabled={modalMode === "view" || saving}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        if (modalMode === "create") handleCreate();
                        if (modalMode === "edit") handleUpdate();
                      }
                    }}
                  />
                </div>
              )}
            </div>

            <div className="card-footer d-flex justify-content-end" style={{ gap: 10 }}>
              {modalMode === "view" ? (
                <>
                  <button type="button" className="btn btn-secondary" onClick={closeModal}>
                    Close
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeModal}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  {modalMode === "create" ? (
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleCreate}
                      disabled={saving || loadingItem}
                    >
                      {saving ? "Creating..." : "Create"}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleUpdate}
                      disabled={saving || loadingItem}
                    >
                      {saving ? "Saving..." : "Save"}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Designation;
